import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Beaker, Check, Ban, ExternalLink, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  listSitesLite, markSamplingFlag, SAMPLING_SCOPES,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";

const SCOPE_LABEL = SAMPLING_SCOPES.reduce((a, s) => ((a[s.value] = s.label), a), {});
const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  sampled: "bg-emerald-100 text-emerald-700",
  skipped: "bg-slate-100 text-slate-500",
};

/** Cross-booking queue of sampling flags grouped by site → scope. */
export default function SamplingQueue() {
  const { user } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const [flags, setFlags]   = useState([]);
  const [sites, setSites]   = useState([]);
  const [siteId, setSiteId] = useState("");
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [tick, setTick]     = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [r, { data: s }] = await Promise.all([
        (async () => {
          let q = supabase
            .from("lbs_sampling_flags")
            .select(
              `id, booking_id, scope, reason, status, created_at, sampled_at,
             site_id, client_id,
             rule:lbs_sampling_rules!lbs_sampling_flags_rule_id_fkey(name,trigger_type),
             booking:lbs_bookings!lbs_sampling_flags_booking_id_fkey(reference_number,scheduled_start,sender_company,sender_name,vehicle_type),
             site:lbs_sites!lbs_sampling_flags_site_id_fkey(name,code)`
            )
            .order("created_at", { ascending: false })
            .limit(500);
          if (viewAsClientId) q = q.eq("client_id", viewAsClientId);
          return q;
        })(),
        listSitesLite(),
      ]);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setFlags(r.data || []);
      setSites(s || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, tick]);

  const filtered = useMemo(() => {
    let arr = flags;
    if (siteId) arr = arr.filter((f) => f.site_id === siteId);
    if (status) arr = arr.filter((f) => f.status === status);
    return arr;
  }, [flags, siteId, status]);

  const counts = useMemo(() => ({
    total:   flags.length,
    pending: flags.filter((f) => f.status === "pending").length,
    sampled: flags.filter((f) => f.status === "sampled").length,
    skipped: flags.filter((f) => f.status === "skipped").length,
  }), [flags]);

  // Group by site → scope.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of filtered) {
      const siteName = f.site?.name || "(unknown site)";
      if (!map.has(siteName)) map.set(siteName, new Map());
      const byScope = map.get(siteName);
      if (!byScope.has(f.scope)) byScope.set(f.scope, []);
      byScope.get(f.scope).push(f);
    }
    return map;
  }, [filtered]);

  const mark = async (id, newStatus) => {
    const { error } = await markSamplingFlag(id, newStatus, user?.id);
    if (error) { toast.error(error.message); return; }
    toast.success(`Marked ${newStatus}.`);
    refresh();
  };

  return (
    <div data-testid="sampling-queue-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Beaker className="h-6 w-6 text-amber-600" /> Sampling queue
          </h1>
          <p className="text-sm text-slate-500">
            Pending samples grouped by site → scope. Clear them as samples are taken.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refresh}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Link to="/admin/sampling-rules">
            <Button variant="outline">Manage rules</Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Chip n={counts.pending} label="Pending" active={status==="pending"} onClick={() => setStatus("pending")} testid="chip-pending" />
        <Chip n={counts.sampled} label="Sampled" active={status==="sampled"} onClick={() => setStatus("sampled")} testid="chip-sampled" />
        <Chip n={counts.skipped} label="Skipped" active={status==="skipped"} onClick={() => setStatus("skipped")} testid="chip-skipped" />
        <Chip n={counts.total}   label="All"     active={status===""}       onClick={() => setStatus("")}        testid="chip-all" />
        <div className="ml-auto w-48">
          <Select value={siteId || "_all_"} onValueChange={(v) => setSiteId(v === "_all_" ? "" : v)}>
            <SelectTrigger data-testid="sq-site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 px-3 py-6">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-400" data-testid="sq-empty">
          No {status || "matching"} flags right now.
        </CardContent></Card>
      ) : (
        <div className="space-y-4" data-testid="sq-groups">
          {Array.from(grouped.entries()).map(([siteName, byScope]) => (
            <Card key={siteName}>
              <CardHeader className="py-2.5">
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  <span>{siteName}</span>
                  <span className="text-xs font-normal text-slate-500 tabular-nums">
                    {Array.from(byScope.values()).reduce((n, a) => n + a.length, 0)} flag(s)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y">
                {Array.from(byScope.entries()).map(([scope, items]) => (
                  <div key={scope}>
                    <div className="px-3 py-1.5 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                      <span>{SCOPE_LABEL[scope] || scope}</span>
                      <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
                    </div>
                    <div className="divide-y">
                      {items.map((f) => (
                        <div key={f.id} className="px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`sq-flag-${f.id}`}>
                          <Badge className={`text-[10px] font-mono ${STATUS_STYLE[f.status] || ""}`}>{f.status}</Badge>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">
                              {f.rule?.name || "(rule deleted)"}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {f.booking?.reference_number ? (
                                <Link to={`/dock/checklist/${f.booking_id}`} className="text-blue-600 hover:underline font-mono inline-flex items-center gap-1">
                                  {f.booking.reference_number} <ExternalLink className="h-3 w-3" />
                                </Link>
                              ) : "—"}
                              {f.booking?.sender_company && <span> · {f.booking.sender_company}</span>}
                              {f.booking?.vehicle_type && <span> · {f.booking.vehicle_type}</span>}
                            </div>
                            {f.reason && <div className="text-[11px] text-slate-500 mt-0.5">{f.reason}</div>}
                          </div>
                          {f.status === "pending" && (
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="outline" onClick={() => mark(f.id, "sampled")} data-testid={`sq-mark-sampled-${f.id}`}>
                                <Check className="h-3.5 w-3.5 mr-1" /> Sampled
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => mark(f.id, "skipped")} data-testid={`sq-mark-skipped-${f.id}`}>
                                <Ban className="h-3.5 w-3.5 mr-1" /> Skip
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ n, label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}>
      {label} <span className="tabular-nums ml-1 opacity-70">{n}</span>
    </button>
  );
}
